import React, { useState } from 'react';
import { useProducts } from '../../context/ProductContext';
import { ArrowLeft, Plus, Minus, ShoppingCart, Package, Star, Truck, Shield, RefreshCw } from 'lucide-react';

export default function ProductDetail({ productId, onBack, onAddToCart }) {
  const { products } = useProducts();
  const product = products.find(p => p.id === productId);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">Product not found</p>
        <button
          onClick={onBack}
          className="mt-4 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700"
        >
          Back to Products
        </button>
      </div>
    );
  }

  // For demo purposes, we'll use the same image multiple times
  // In production, you'd have multiple product images
  const images = product.image 
    ? [product.image, product.image, product.image] 
    : [];

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= product.stock) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      onAddToCart(product);
    }
    alert(`Added ${quantity} ${product.name}${quantity > 1 ? 's' : ''} to cart!`);
  };

  const handleBuyNow = () => {
    handleAddToCart();
    // In a real app, this would redirect to checkout
    alert('Redirecting to checkout...');
  };

  const stockStatus = product.stock > 10 ? 'In Stock' : product.stock > 0 ? 'Low Stock' : 'Out of Stock';
  const stockColor = product.stock > 10 ? 'text-green-600' : product.stock > 0 ? 'text-yellow-600' : 'text-red-600';

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-red-600 mb-6"
      >
        <ArrowLeft size={20} />
        Back to Products
      </button>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-8">
          {/* Left Column - Images */}
          <div>
            {/* Main Image */}
            <div className="bg-gray-100 rounded-lg overflow-hidden mb-4 aspect-square flex items-center justify-center">
              {product.image ? (
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Package size={120} className="text-gray-300" />
              )}
            </div>

            {/* Thumbnail Images */}
            {images.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`bg-gray-100 rounded-lg overflow-hidden aspect-square ${
                      selectedImage === index ? 'ring-2 ring-red-600' : ''
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Details */}
          <div>
            {/* Category */}
            <p className="text-sm text-gray-500 mb-2">{product.category}</p>

            {/* Product Name */}
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} size={20} className="fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-gray-600">(128 reviews)</span>
            </div>

            {/* Price */}
            <div className="mb-6">
              <span className="text-4xl font-bold text-red-600">${product.price}</span>
              <span className="text-gray-500 ml-2 line-through">$${(product.price * 1.2).toFixed(2)}</span>
            </div>

            {/* Stock Status */}
            <div className="mb-6">
              <span className={`font-semibold ${stockColor}`}>{stockStatus}</span>
              <span className="text-gray-600 ml-2">({product.stock} available)</span>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-600">{product.description}</p>
            </div>

            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="block font-semibold text-gray-900 mb-2">Quantity</label>
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    className="p-3 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Minus size={20} />
                  </button>
                  <span className="px-6 font-semibold text-lg">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= product.stock}
                    className="p-3 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={20} />
                  </button>
                </div>
                <span className="text-gray-600">
                  Total: <span className="font-semibold text-gray-900">${(product.price * quantity).toFixed(2)}</span>
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="flex-1 flex items-center justify-center gap-2 bg-gray-900 text-white py-4 rounded-lg hover:bg-gray-800 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <ShoppingCart size={20} />
                Add to Cart
              </button>
              <button
                onClick={handleBuyNow}
                disabled={product.stock === 0}
                className="flex-1 bg-red-600 text-white py-4 rounded-lg hover:bg-red-700 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Buy Now
              </button>
            </div>

            {/* Features */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-gray-900 mb-4">Why Buy From Us?</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Truck className="text-red-600" size={24} />
                  <div>
                    <p className="font-medium">Free Shipping</p>
                    <p className="text-sm text-gray-500">On orders over $50</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="text-red-600" size={24} />
                  <div>
                    <p className="font-medium">Secure Payment</p>
                    <p className="text-sm text-gray-500">100% secure transactions</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <RefreshCw className="text-red-600" size={24} />
                  <div>
                    <p className="font-medium">Easy Returns</p>
                    <p className="text-sm text-gray-500">30-day return policy</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Info Section */}
        <div className="border-t border-gray-200 p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Product Details</h3>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Category:</dt>
                  <dd className="font-medium">{product.category}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">SKU:</dt>
                  <dd className="font-medium">MP-{product.id}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Availability:</dt>
                  <dd className={`font-medium ${stockColor}`}>{stockStatus}</dd>
                </div>
              </dl>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Shipping Info</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Standard Delivery: 3-5 business days</li>
                <li>• Express Delivery: 1-2 business days</li>
                <li>• Free shipping on orders over $50</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-4">Return Policy</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• 30-day return window</li>
                <li>• Items must be unused</li>
                <li>• Original packaging required</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products Section (Optional) */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">You May Also Like</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products
            .filter(p => p.category === product.category && p.id !== product.id)
            .slice(0, 4)
            .map(relatedProduct => (
              <div
                key={relatedProduct.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden cursor-pointer"
                onClick={() => window.scrollTo(0, 0)}
              >
                <div className="h-48 bg-gray-100 flex items-center justify-center">
                  {relatedProduct.image ? (
                    <img src={relatedProduct.image} alt={relatedProduct.name} className="w-full h-full object-cover" />
                  ) : (
                    <Package size={64} className="text-gray-300" />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg mb-2">{relatedProduct.name}</h3>
                  <p className="text-xl font-bold text-red-600">${relatedProduct.price}</p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}